import { useState, useEffect, useMemo } from "react";
import { swalSuccess, swalError, swalConfirm } from "../../../utils/swalAlert.js";

import {
  fetchTemplateSections,
  createTemplateSection,
  updateTemplateSection,
  deleteTemplateSection,
  fetchTemplates,
} from "../../../api/admin/coreBasics";

import EditModal from "../../AdminPanel/components/EditModal.jsx";

import "../../../styles/AdminPanel/CoreBasic.css";

const PAGE_SIZE = 8;

export default function TemplateSections() {
  const [sections, setSections] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [jsonText, setJsonText] = useState("{}");
  const [page, setPage] = useState(1);

  const [form, setForm] = useState({
    template: "",
    page_key: "",
    section_key: "",
    section_type: "text",
    repeatable: false,
    config_json: {},
    sort_order: 0,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  /* =========================================================
     LOAD
     ========================================================= */

  const load = async () => {
    try {
      const [sectionsRes, templatesRes] = await Promise.all([
        fetchTemplateSections(),
        fetchTemplates(),
      ]);

      setSections(sectionsRes.data || []);
      setTemplates(templatesRes.data || []);
    } catch (err) {
      console.error(err);

      swalError({ title: "خطا در دریافت اطلاعات" });
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* =========================================================
     PAGINATION
     ========================================================= */

  const totalCount = sections.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);

  useEffect(() => {
    if (page !== pageSafe) setPage(pageSafe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  const pageNumbers = useMemo(() => {
    const nums = [];
    for (let n = 1; n <= totalPages; n++) nums.push(n);
    return nums;
  }, [totalPages]);

  const paged = useMemo(() => {
    const start = (pageSafe - 1) * PAGE_SIZE;
    return sections.slice(start, start + PAGE_SIZE);
  }, [sections, pageSafe]);

  /* =========================================================
     ADD
     ========================================================= */

  const openAdd = () => {
    setEditing(null);

    const blankForm = {
      template: "",
      page_key: "",
      section_key: "",
      section_type: "text",
      repeatable: false,
      config_json: {},
      sort_order: 0,
    };

    setForm(blankForm);
    setJsonText("{}");
    setIsModalOpen(true);
  };

  /* =========================================================
     EDIT
     ========================================================= */

  const openEdit = (item) => {
    setEditing(item);

    setForm({
      template: item.template ?? "",
      page_key: item.page_key ?? "",
      section_key: item.section_key ?? "",
      section_type: item.section_type ?? "text",
      repeatable: item.repeatable ?? false,
      config_json: item.config_json ?? {},
      sort_order: item.sort_order ?? 0,
    });

    setJsonText(JSON.stringify(item.config_json ?? {}, null, 2));

    setIsModalOpen(true);
  };

  /* =========================================================
     SAVE
     ========================================================= */

  const save = async () => {
    let parsedJson = {};

    try {
      parsedJson = JSON.parse(jsonText);
    } catch (err) {
      swalError({ title: "ساختار JSON معتبر نیست", text: err.message });

      return;
    }

    const payload = {
      ...form,
      config_json: parsedJson,
    };

    try {
      setLoading(true);

      if (editing) {
        await updateTemplateSection(editing.section_id, payload);
      } else {
        await createTemplateSection(payload);
      }

      await swalSuccess({ title: "سکشن ذخیره شد" });

      setIsModalOpen(false);
      setEditing(null);

      await load();
    } catch (err) {
      console.error(err);

      swalError({ title: "خطا در ذخیره اطلاعات" });
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     DELETE
     ========================================================= */

  const remove = async (id) => {
    const confirmed = await swalConfirm({
      title: "حذف سکشن؟",
      text: "این عملیات قابل بازگشت نیست",
      confirmText: "بله، حذف شود",
      cancelText: "انصراف",
      danger: true,
    });

    if (!confirmed) return;

    try {
      await deleteTemplateSection(id);

      await swalSuccess({ title: "سکشن حذف شد" });

      await load();
    } catch (err) {
      console.error(err);

      swalError({ title: "خطا در حذف سکشن" });
    }
  };

  /* =========================================================
     SECTION TYPE LABEL
     ========================================================= */

  const sectionTypeLabel = (type) => {
    const labels = {
      text: "متن",
      image: "تصویر",
      map: "نقشه",
      form: "فرم",
      menu: "منو",
      html: "HTML",
      social: "شبکه اجتماعی",
      slider: "اسلایدر",
      product_list: "لیست محصولات",
      category_grid: "گرید دسته‌بندی",
      text_image: "متن + تصویر",
      faq: "FAQ",
    };

    return labels[type] || type || "---";
  };

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <>
      {/* =====================================================
          HEADER
          ===================================================== */}

      <div className="page-header-row">
        <h2 className="page-title">سکشن‌های تمپلیت</h2>

        <div className="add-button-wrapper">
          <button type="button" className="btn-glass" onClick={openAdd}>
            افزودن سکشن
          </button>
        </div>
      </div>

      {/* =====================================================
          TABLE
          ===================================================== */}

      <div className="table-wrapper">
        <table className="user-table">
          <thead>
            <tr>
              <th>#</th>
              <th>تمپلیت</th>
              <th>صفحه</th>
              <th>کلید سکشن</th>
              <th>نوع</th>
              <th>تکرارشونده</th>
              <th>ترتیب</th>
              <th>عملیات</th>
            </tr>
          </thead>

          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  style={{
                    textAlign: "center",
                    padding: "28px",
                    color: "rgba(43, 18, 76, 0.45)",
                  }}
                >
                  هیچ سکشنی ثبت نشده است
                </td>
              </tr>
            ) : (
              paged.map((section, index) => {
                const template = templates.find(
                  (template) => template.template_id === section.template,
                );

                return (
                  <tr key={section.section_id}>
                    {/* شماره */}
                    <td>{(pageSafe - 1) * PAGE_SIZE + index + 1}</td>

                    {/* تمپلیت */}
                    <td>{template?.title || "---"}</td>

                    {/* صفحه */}
                    <td>
                      <code>{section.page_key || "---"}</code>
                    </td>

                    {/* کلید سکشن */}
                    <td>
                      <code>{section.section_key || "---"}</code>
                    </td>

                    {/* نوع */}
                    <td>{sectionTypeLabel(section.section_type)}</td>

                    {/* تکرارشونده */}
                    <td>
                      <span
                        className={`template-status ${
                          section.repeatable ? "is-active" : "is-inactive"
                        }`}
                      >
                        <span
                          className="template-status-dot"
                          aria-hidden="true"
                        />

                        {section.repeatable ? "بله" : "خیر"}
                      </span>
                    </td>

                    {/* ترتیب */}
                    <td>{section.sort_order ?? 0}</td>

                    {/* عملیات */}
                    <td>
                      <div className="actions-cell">
                        {/* ویرایش */}
                        <button
                          type="button"
                          className="icon-action icon-edit"
                          onClick={() => openEdit(section)}
                          title="ویرایش"
                          aria-label={`ویرایش ${section.section_key || "سکشن"}`}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                          </svg>
                        </button>

                        {/* حذف */}
                        <button
                          type="button"
                          className="icon-action icon-delete"
                          onClick={() => remove(section.section_id)}
                          title="حذف"
                          aria-label={`حذف ${section.section_key || "سکشن"}`}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M3 6h18" />
                            <path d="M8 6V4h8v2" />
                            <path d="M19 6l-1 14H6L5 6" />
                            <path d="M10 11v5" />
                            <path d="M14 11v5" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {totalCount > 0 && (
          <div className="orders-pagination">
            <span className="pagination-info">
              {totalCount.toLocaleString("fa-IR")} سکشن — صفحه{" "}
              {pageSafe.toLocaleString("fa-IR")} از{" "}
              {totalPages.toLocaleString("fa-IR")}
            </span>
            <div className="pagination-controls">
              <button
                className="page-btn"
                disabled={pageSafe === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="صفحه قبل"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 6l-6 6 6 6" />
                </svg>
              </button>
              {pageNumbers.map((n) => (
                <button
                  key={n}
                  className={`page-btn number${n === pageSafe ? " active" : ""}`}
                  onClick={() => setPage(n)}
                >
                  {n.toLocaleString("fa-IR")}
                </button>
              ))}
              <button
                className="page-btn"
                disabled={pageSafe === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="صفحه بعد"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* =====================================================
          MODAL
          ===================================================== */}

      {isModalOpen && (
        <EditModal
          onClose={() => {
            if (!loading) {
              setIsModalOpen(false);
            }
          }}
        >
          <h3 className="modal-title">
            {editing ? "ویرایش سکشن" : "افزودن سکشن"}
          </h3>

          {/* تمپلیت */}
          <select
            value={form.template}
            onChange={(e) =>
              setForm({
                ...form,
                template: e.target.value,
              })
            }
          >
            <option value="">انتخاب تمپلیت</option>

            {templates.map((template) => (
              <option key={template.template_id} value={template.template_id}>
                {template.title}
              </option>
            ))}
          </select>

          {/* کلید صفحه */}
          <input
            type="text"
            placeholder="کلید صفحه"
            value={form.page_key}
            onChange={(e) =>
              setForm({
                ...form,
                page_key: e.target.value,
              })
            }
          />

          {/* کلید سکشن */}
          <input
            type="text"
            placeholder="کلید سکشن"
            value={form.section_key}
            onChange={(e) =>
              setForm({
                ...form,
                section_key: e.target.value,
              })
            }
          />

          {/* نوع سکشن */}
          <select
            value={form.section_type}
            onChange={(e) =>
              setForm({
                ...form,
                section_type: e.target.value,
              })
            }
          >
            <option value="text">متن</option>
            <option value="image">تصویر</option>
            <option value="map">نقشه</option>
            <option value="form">فرم</option>
            <option value="menu">منو</option>
            <option value="html">HTML</option>
            <option value="social">شبکه اجتماعی</option>
            <option value="slider">اسلایدر</option>
            <option value="product_list">لیست محصولات</option>
            <option value="category_grid">گرید دسته‌بندی</option>
            <option value="text_image">متن + تصویر</option>
            <option value="faq">FAQ (آکاردئون)</option>
          </select>

          {/* JSON */}
          <textarea
            placeholder='config_json مثل {"fields": [{"name": "title","type": "text"}]}'
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            style={{
              minHeight: 180,
              direction: "ltr",
              fontFamily: "monospace",
              fontSize: 13,
              resize: "vertical",
            }}
          />

          {/* ترتیب */}
          <input
            type="number"
            placeholder="ترتیب نمایش"
            value={form.sort_order}
            onChange={(e) =>
              setForm({
                ...form,
                sort_order: Number(e.target.value),
              })
            }
          />

          {/* تکرارشونده */}
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.repeatable}
              onChange={(e) =>
                setForm({
                  ...form,
                  repeatable: e.target.checked,
                })
              }
            />
            سکشن تکرار شود{" "}
          </label>

          {/* دکمه‌ها */}
          <div className="modal-buttons">
            <button
              type="button"
              className="btn-glass"
              onClick={save}
              disabled={loading}
            >
              {loading ? "در حال ذخیره..." : "ذخیره"}
            </button>

            <button
              type="button"
              className="btn btn-cancel"
              onClick={() => setIsModalOpen(false)}
              disabled={loading}
            >
              انصراف
            </button>
          </div>
        </EditModal>
      )}
    </>
  );
}
